import { Link } from "react-router-dom";
import {
  ArrowUpRight,
  Compass,
  DraftingCompass,
  Map,
  Plane,
  ScanLine,
  FileText,
  Leaf,
  Briefcase,
  Award,
  Target,
  Users,
  Calendar,
} from "lucide-react";
import { Reveal, SectionLabel, SectionTitle } from "@/components/Reveal";
import { studio, services } from "@/data/site";
import { latestProjects, formatPublishedAt } from "@/data/lavori";

const ICONS = {
  Compass,
  DraftingCompass,
  Map,
  Plane,
  ScanLine,
  FileText,
  Leaf,
  Briefcase,
};

export default function Home() {
  return (
    <main data-testid="home-page" className="bg-slate-50">
      {/* HERO */}
      <section
        data-testid="home-hero"
        className="relative min-h-[100vh] pt-32 pb-20 overflow-hidden bg-slate-900"
      >
        <div className="absolute inset-0 z-0">
          <img
            src="/images/home/2.jpg"
            alt="Territorio della provincia di Treviso"
            className="w-full h-full object-cover"
            onError={(e) => (e.currentTarget.style.display = "none")}
          />
          <div className="absolute inset-0 bg-gradient-to-r from-slate-950/90 via-slate-950/75 to-slate-900/40" />
          <div className="absolute inset-0 grain-bg" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-12 items-center min-h-[80vh]">
          <div className="lg:col-span-7 text-white">
            <Reveal>
              <div className="overline text-amber-400">
                Studio Tecnico · Villorba (TV) · Dal 1990
              </div>
            </Reveal>
            <Reveal delay={0.1}>
              <h1 className="font-serif font-medium mt-6 text-5xl sm:text-6xl lg:text-7xl tracking-tight leading-[1.02]">
                Geometria,<br />
                <em className="not-italic text-amber-300">precisione</em>{" "}
                e territorio.
              </h1>
            </Reveal>
            <Reveal delay={0.2}>
              <p className="mt-8 text-lg lg:text-xl text-slate-200 max-w-2xl leading-relaxed">
                Trent'anni di progettazione, consulenza e rilievi topografici
                in tutto il triveneto. Dal classico rilievo strumentale alla
                fotogrammetria con drone, fino allo scanning 3D per ogni
                esigenza tecnica.
              </p>
            </Reveal>
            <Reveal delay={0.3}>
              <div className="mt-10 flex flex-wrap gap-4">
                <Link
                  to="/servizi"
                  data-testid="hero-cta-services"
                  className="inline-flex items-center gap-2 bg-white text-slate-900 px-7 py-3.5 rounded-full font-medium hover:bg-amber-300 transition-colors"
                >
                  Scopri i servizi <ArrowUpRight size={16} />
                </Link>
                <Link
                  to="/contatti"
                  data-testid="hero-cta-contact"
                  className="inline-flex items-center gap-2 border border-white/40 text-white px-7 py-3.5 rounded-full font-medium hover:bg-white/10 transition-colors"
                >
                  Contattaci
                </Link>
              </div>
            </Reveal>
          </div>

          <div className="hidden lg:block lg:col-span-5">
            <Reveal delay={0.4}>
              <div className="relative">
                <div className="aspect-[4/5] img-zoom rounded-sm overflow-hidden border border-white/10 shadow-2xl">
                  <img
                    src="/images/home/8-Ingresso ufficio.jpg"
                    alt="Studio Cecconato Geometra Michele"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="absolute -bottom-6 -left-6 bg-white px-6 py-5 shadow-xl border border-slate-200">
                  <div className="text-4xl font-serif text-slate-900">{studio.experienceYears}</div>
                  <div className="text-[11px] tracking-[0.18em] uppercase text-slate-500 mt-1">
                    Anni di esperienza
                  </div>
                </div>
              </div>
            </Reveal>
          </div>
        </div>

        {/* Bottom strip */}
        <div className="absolute z-10 bottom-0 inset-x-0 border-t border-white/10 bg-slate-950/60 backdrop-blur-md hidden md:block">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5 grid grid-cols-3 gap-8 text-white">
            <div>
              <div className="text-[10px] tracking-[0.22em] uppercase text-amber-300">
                Albo professionale
              </div>
              <div className="mt-1 text-sm">{studio.albo}</div>
            </div>
            <div>
              <div className="text-[10px] tracking-[0.22em] uppercase text-amber-300">
                Sede
              </div>
              <div className="mt-1 text-sm">{studio.address}</div>
            </div>
            <div>
              <div className="text-[10px] tracking-[0.22em] uppercase text-amber-300">
                Operativi
              </div>
              <div className="mt-1 text-sm">In tutto il triveneto</div>
            </div>
          </div>
        </div>
      </section>

      {/* INTRO STUDIO */}
      <section className="py-24 lg:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <Reveal>
              <SectionLabel>Lo studio</SectionLabel>
              <SectionTitle className="mt-4">
                Una bottega<br /> tecnica radicata <em className="not-italic text-amber-800">nel territorio.</em>
              </SectionTitle>
            </Reveal>
          </div>
          <div className="lg:col-span-7 lg:pl-10 lg:border-l lg:border-slate-200">
            <Reveal delay={0.1}>
              <p className="text-lg leading-relaxed text-slate-700">
                Lo studio tecnico <strong>Cecconato Geometra Michele</strong> si
                trova a Villorba, in provincia di Treviso, e vanta
                un'esperienza trentennale negli ambiti della progettazione,
                della consulenza e dei rilievi topografici. Opera prevalentemente
                nel triveneto, fornendo servizi a committenti pubblici e privati
                in ambito civile, rurale, commerciale e industriale.
              </p>
              <p className="mt-6 text-lg leading-relaxed text-slate-700">
                La reciproca collaborazione con altri tecnici, specialisti
                nelle rispettive discipline, permette di svolgere incarichi di
                vario genere garantendo al cliente una soluzione completa ed
                efficace alle specifiche richieste.
              </p>
            </Reveal>

            <div className="mt-12 grid sm:grid-cols-3 gap-6">
              {[
                { icon: Award, h: "Esperienza", p: "Oltre 30 anni di pratica professionale continua." },
                { icon: Target, h: "Precisione", p: "Strumentazione di ultima generazione e protocolli rigorosi." },
                { icon: Users, h: "Rete", p: "Network di professionisti per ogni disciplina." },
              ].map((b, i) => (
                <Reveal key={b.h} delay={0.1 * i}>
                  <div className="border-t-2 border-slate-900 pt-5">
                    <b.icon size={22} className="text-amber-800" />
                    <div className="font-serif text-xl mt-3 text-slate-900">{b.h}</div>
                    <div className="text-sm text-slate-600 mt-2 leading-relaxed">{b.p}</div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* SERVIZI */}
      <section id="servizi" className="py-24 lg:py-32 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-12 gap-8 mb-14">
            <div className="lg:col-span-6">
              <Reveal>
                <SectionLabel>Cosa facciamo</SectionLabel>
                <SectionTitle className="mt-4">
                  Otto aree di competenza, <em className="not-italic text-amber-800">un unico interlocutore.</em>
                </SectionTitle>
              </Reveal>
            </div>
            <div className="lg:col-span-5 lg:col-start-8 flex items-end">
              <Reveal delay={0.1}>
                <p className="text-base text-slate-600 leading-relaxed">
                  Dalla progettazione edile alla riqualificazione energetica,
                  dal rilievo topografico alla scansione laser 3D. Una gamma
                  completa di servizi tecnici per ogni esigenza pubblica e
                  privata.
                </p>
              </Reveal>
            </div>
          </div>

          {/* Bento grid */}
          <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
            {services.map((s, idx) => {
              const Icon = ICONS[s.icon] || Compass;
              const layouts = [
                "md:col-span-7",
                "md:col-span-5",
                "md:col-span-4",
                "md:col-span-4",
                "md:col-span-4",
                "md:col-span-5",
                "md:col-span-4",
                "md:col-span-3",
              ];
              return (
                <Reveal key={s.slug} delay={idx * 0.05} className={layouts[idx]}>
                  <Link
                    to={`/servizi#${s.slug}`}
                    data-testid={`home-service-${s.slug}`}
                    className="group relative h-full bg-white border border-slate-200 p-7 lg:p-9 flex flex-col justify-between hover:border-slate-900 hover:shadow-lg transition-all duration-300 hover:-translate-y-0.5"
                  >
                    <div>
                      <Icon size={28} className="text-amber-800" />
                      <h3 className="font-serif text-2xl lg:text-3xl mt-6 text-slate-900 leading-tight">
                        {s.title}
                      </h3>
                      <p className="mt-4 text-sm text-slate-600 leading-relaxed">
                        {s.short}
                      </p>
                    </div>
                    <div className="mt-8 flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-slate-900">
                      Approfondisci
                      <ArrowUpRight size={14} className="transition-transform group-hover:translate-x-1 group-hover:-translate-y-1" />
                    </div>
                  </Link>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* MISSION + photo */}
      <section className="bg-slate-900 text-white py-24 lg:py-32 relative overflow-hidden">
        <div className="absolute inset-0 grain-bg opacity-50" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-12 items-center relative">
          <div className="lg:col-span-7">
            <Reveal>
              <SectionLabel className="text-amber-400">Mission</SectionLabel>
              <h2 className="font-serif font-medium mt-5 text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[1.05]">
                Individuare la <em className="not-italic text-amber-300">miglior soluzione</em>{" "}
                per le esigenze di ogni cliente.
              </h2>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="mt-8 text-lg text-slate-300 leading-relaxed max-w-2xl">
                Impieghiamo metodi adeguati e rispettiamo le tempistiche senza
                intaccare la qualità e il risultato finale del lavoro: con
                sicurezza, affidabilità e nel pieno rispetto dei costi
                prefissati.
              </p>
            </Reveal>
            <Reveal delay={0.2}>
              <Link
                to="/chi-siamo"
                data-testid="mission-cta"
                className="mt-10 inline-flex items-center gap-2 border-b border-amber-300 text-amber-300 pb-1 hover:gap-3 transition-all"
              >
                Conosci il geometra Cecconato <ArrowUpRight size={16} />
              </Link>
            </Reveal>
          </div>
          <div className="lg:col-span-5">
            <Reveal delay={0.2}>
              <div className="aspect-square img-zoom border border-white/10">
                <img
                  src="/images/home/9-Interno ufficio.JPG"
                  alt="Interno dello studio Cecconato"
                  className="w-full h-full object-cover"
                />
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ULTIMI LAVORI */}
      <section className="py-24 lg:py-32 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-12 gap-8 mb-14">
            <div className="lg:col-span-7">
              <Reveal>
                <SectionLabel>Ultimi lavori</SectionLabel>
                <SectionTitle className="mt-4">
                  Una selezione recente, <em className="not-italic text-amber-800">dal nostro portfolio.</em>
                </SectionTitle>
              </Reveal>
            </div>
            <div className="lg:col-span-4 lg:col-start-9 flex items-end">
              <Reveal delay={0.1}>
                <Link
                  to="/lavori"
                  data-testid="home-latest-all"
                  className="inline-flex items-center gap-2 text-sm font-medium text-slate-900 border-b border-slate-900 pb-1 hover:gap-3 transition-all"
                >
                  Tutti i lavori <ArrowUpRight size={14} />
                </Link>
              </Reveal>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 lg:gap-6">
            {latestProjects.slice(0, 4).map((p, i) => (
              <Reveal key={`${p.categorySlug}-${p.name}`} delay={i * 0.06}>
                <Link
                  to={`/lavori/${p.categorySlug}`}
                  data-testid={`home-latest-${i}`}
                  className="group block bg-white border border-slate-200 hover:border-slate-900 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lg h-full"
                >
                  <div className="aspect-[4/3] img-zoom bg-slate-100 overflow-hidden">
                    {p.cover && (
                      <img
                        src={p.cover}
                        alt={p.name}
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                    )}
                  </div>
                  <div className="p-5 lg:p-6">
                    <div className="flex items-center gap-2 text-[10px] tracking-[0.18em] uppercase text-amber-800 font-semibold">
                      <Calendar size={11} />
                      {formatPublishedAt(p.published_at)}
                    </div>
                    <div className="text-[11px] tracking-[0.16em] uppercase text-slate-400 mt-2">
                      {p.categoryShortTitle}
                    </div>
                    <h3 className="font-serif text-xl lg:text-2xl mt-2 text-slate-900 leading-tight">
                      {p.name}
                    </h3>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA STRIP */}
      <section className="py-20 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Reveal>
            <SectionLabel>Hai un progetto?</SectionLabel>
            <h2 className="font-serif mt-4 text-4xl md:text-5xl text-slate-900 leading-tight">
              Scriviamoci. Il primo sopralluogo<br />
              <em className="not-italic text-amber-800">è una conversazione.</em>
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <Link
              to="/contatti"
              data-testid="bottom-cta-contact"
              className="mt-10 inline-flex items-center gap-2 bg-slate-900 text-white px-8 py-4 rounded-full font-medium hover:bg-amber-800 transition-colors"
            >
              Vai ai contatti <ArrowUpRight size={16} />
            </Link>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
